import React from "react";
import Footer from "../Components/Footer";
import SEO from "../Components/SEO";

const PrivacyPolicy = () => (
  <>
    <SEO
      title="Privacy Policy"
      description="Privacy policy for Hear Me Out Music Co."
      type="article"
    />
    <div className="min-h-screen bg-[#111111] text-white px-4 py-12 flex flex-col items-center">
      <div className="w-full max-w-3xl">
        <h1 className="text-4xl md:text-5xl font-bold text-primary-green mb-4 border-b-2 border-primary-green pb-2">
          LinkedInsight Privacy Policy
        </h1>
        <p className="italic text-white/60 mb-8">Last Updated: May 22, 2025</p>
        <p className="mb-6">
          Welcome to LinkedInsight. We respect your privacy and are committed to
          protecting your personal data. This privacy policy will inform you
          about how we look after your personal data when you use our service
          and tell you about your privacy rights.
        </p>
        <h2 className="text-2xl text-primary-green font-semibold mt-10 mb-2">
          1. Important Information and Who We Are
        </h2>
        <p className="mb-6">
          LinkedInsight is a professional networking intelligence platform that
          helps users understand their LinkedIn network's engagement patterns.
          This policy applies to all data collected through our platform.
        </p>
        <h2 className="text-2xl text-primary-green font-semibold mt-10 mb-2">
          2. Data We Collect
        </h2>
        <p className="mb-2">
          We collect the following types of information when you use
          LinkedInsight:
        </p>
        <ul className="list-disc list-inside mb-6 space-y-2">
          <li>
            <strong>Account Information:</strong> Your name, email address, and
            other information you provide when creating an account.
          </li>
          <li>
            <strong>LinkedIn Connection Data:</strong> With your explicit
            consent, we access data about your LinkedIn connections, including
            names, industries, and engagement patterns.
          </li>
          <li>
            <strong>Engagement Analytics:</strong> Information about which
            content your connections interact with and engagement patterns we
            observe.
          </li>
          <li>
            <strong>Usage Information:</strong> Information about how you use
            our platform, including features accessed and time spent.
          </li>
        </ul>
        <h2 className="text-2xl text-primary-green font-semibold mt-10 mb-2">
          3. How We Use Your Data
        </h2>
        <p className="mb-2">We use your data for the following purposes:</p>
        <ul className="list-disc list-inside mb-6 space-y-2">
          <li>To provide and maintain our service</li>
          <li>
            To analyze connection engagement patterns and provide insights
          </li>
          <li>To improve and personalize your experience</li>
          <li>To communicate with you about your account or our service</li>
          <li>To comply with legal obligations</li>
        </ul>
        <h2 className="text-2xl text-primary-green font-semibold mt-10 mb-2">
          4. Data Sharing and Transfers
        </h2>
        <p className="mb-2">
          We do not sell your personal data. We may share your data with:
        </p>
        <ul className="list-disc list-inside mb-6 space-y-2">
          <li>
            <strong>Service Providers:</strong> Third parties who provide
            services on our behalf, such as hosting providers.
          </li>
          <li>
            <strong>Legal Requirements:</strong> When required by law or to
            protect our rights.
          </li>
        </ul>
        <p className="mb-6">
          All service providers are contractually required to use your data only
          for the provision of their services and in compliance with applicable
          privacy laws.
        </p>
        <h2 className="text-2xl text-primary-green font-semibold mt-10 mb-2">
          5. Data Security
        </h2>
        <p className="mb-2">
          We have implemented appropriate technical and organizational measures
          to secure your personal data from accidental loss, unauthorized
          access, alteration, or disclosure. These measures include:
        </p>
        <ul className="list-disc list-inside mb-6 space-y-2">
          <li>End-to-end encryption of sensitive data</li>
          <li>Regular security assessments</li>
          <li>Access controls and authentication requirements</li>
          <li>Regular security training for all staff</li>
        </ul>
        <h2 className="text-2xl text-primary-green font-semibold mt-10 mb-2">
          6. Data Retention
        </h2>
        <p className="mb-2">
          We will retain your personal data only for as long as necessary to
          fulfill the purposes for which we collected it. When determining
          retention periods, we consider:
        </p>
        <ul className="list-disc list-inside mb-6 space-y-2">
          <li>The amount, nature, and sensitivity of the data</li>
          <li>
            The potential risk of harm from unauthorized use or disclosure
          </li>
          <li>The purposes for which we process the data</li>
          <li>Whether we can achieve those purposes through other means</li>
          <li>Legal, regulatory, and contractual requirements</li>
        </ul>
        <h2 className="text-2xl text-primary-green font-semibold mt-10 mb-2">
          7. Your Legal Rights
        </h2>
        <p className="mb-2">
          Depending on your location, you may have the following rights
          regarding your personal data:
        </p>
        <ul className="list-disc list-inside mb-6 space-y-2">
          <li>
            <strong>Access:</strong> Request a copy of your personal data.
          </li>
          <li>
            <strong>Correction:</strong> Request correction of inaccurate data.
          </li>
          <li>
            <strong>Erasure:</strong> Request deletion of your data in certain
            circumstances.
          </li>
          <li>
            <strong>Restriction:</strong> Request limited processing of your
            data.
          </li>
          <li>
            <strong>Data Portability:</strong> Request transfer of your data to
            you or a third party.
          </li>
          <li>
            <strong>Objection:</strong> Object to processing of your data.
          </li>
          <li>
            <strong>Withdraw Consent:</strong> Withdraw consent at any time
            where we rely on consent to process your data.
          </li>
        </ul>
        <h2 className="text-2xl text-primary-green font-semibold mt-10 mb-2">
          8. LinkedIn Data
        </h2>
        <p className="mb-2">
          Our service integrates with LinkedIn and processes data from your
          LinkedIn account with your explicit permission. We:
        </p>
        <ul className="list-disc list-inside mb-6 space-y-2">
          <li>Only access the data necessary for our service functionality</li>
          <li>
            Process LinkedIn data in accordance with LinkedIn's terms of service
            and API policies
          </li>
          <li>
            Never modify your LinkedIn data without your explicit permission
          </li>
          <li>
            Provide clear methods to disconnect your LinkedIn account at any
            time
          </li>
        </ul>
        <h2 className="text-2xl text-primary-green font-semibold mt-10 mb-2">
          9. Cookies and Similar Technologies
        </h2>
        <p className="mb-6">
          We use cookies and similar tracking technologies to enhance your
          experience. You can set your browser to refuse all or some browser
          cookies, but this may prevent some parts of our service from
          functioning properly.
        </p>
        <h2 className="text-2xl text-primary-green font-semibold mt-10 mb-2">
          10. Children's Privacy
        </h2>
        <p className="mb-6">
          Our service is not intended for individuals under 16 years of age, and
          we do not knowingly collect personal data from children.
        </p>
        <h2 className="text-2xl text-primary-green font-semibold mt-10 mb-2">
          11. Changes to This Privacy Policy
        </h2>
        <p className="mb-6">
          We may update this privacy policy from time to time. We will notify
          you of any changes by posting the new policy on this page and updating
          the "last updated" date.
        </p>
        <div className="bg-white/5 rounded-lg p-6 mt-10">
          <h2 className="text-2xl text-primary-green font-semibold mb-2">
            12. Contact Us
          </h2>
          <p className="mb-2">
            If you have any questions about this privacy policy or our privacy
            practices, please contact us at:
          </p>
          <p className="text-white/80">
            Email: privacy@linkedinsight.com
            <br />
            [YOUR COMPANY NAME]
            <br />
            [YOUR COMPANY ADDRESS]
          </p>
        </div>
      </div>
    </div>
    <Footer />
  </>
);

export default PrivacyPolicy;
